import React from 'react';
import { Text, Pill } from '@clockout-dev/design-elements';
import { formatLongDate } from '../../../../../../utils/dates';

/**
 * Member-specific free-advance promo groups — constants shared by
 * PromoGroupsSection / PromoGroupRow / PromoGroupDrawer.
 *
 * `WINDOW_UNIT.value` strings must match clockout-service's
 * `Constants.FREE_ADVANCE_PROMO.WINDOW_UNIT` exactly — they're interpolated
 * server-side into a Postgres `::interval` cast.
 */
export const PROMO_WINDOW_UNIT_OPTIONS = [
    { value: 'days', label: 'days', singular: 'day', plural: 'days' },
    { value: 'weeks', label: 'weeks', singular: 'week', plural: 'weeks' },
    { value: 'months', label: 'months', singular: 'month', plural: 'months' },
];

export function pluralizeWindowUnit(value, unit) {
    const option = PROMO_WINDOW_UNIT_OPTIONS.find((opt) => opt.value === unit);
    if (!option) return unit;
    return Number(value) === 1 ? option.singular : option.plural;
}

/**
 * Composes the same style of copy FreeAdvancesPanel uses for General rules
 * ("First {count} advances are free at {condition}"), for a group's rule.
 */
export function formatPromoRuleSentence({ free_advance_count, window_value, window_unit }) {
    const unitLabel = pluralizeWindowUnit(window_value, window_unit);
    return `first ${free_advance_count} advances are free at first ${window_value} ${unitLabel}`;
}

// Mirrors clockout-service Constants.FREE_ADVANCE_PROMO.STATE.
export const PROMO_STATE = {
    ACTIVE: 'active',
    EXHAUSTED: 'exhausted',
    EXPIRED: 'expired',
    PENDING: 'pending',
    INACTIVE: 'inactive',
    DISABLED: 'disabled',
    NONE: 'none',
};

export const PROMO_STATE_LABELS = {
    [PROMO_STATE.ACTIVE]: 'Active',
    [PROMO_STATE.EXHAUSTED]: 'Exhausted',
    [PROMO_STATE.EXPIRED]: 'Expired',
    [PROMO_STATE.PENDING]: 'Pending',
    [PROMO_STATE.INACTIVE]: 'Removed',
    [PROMO_STATE.DISABLED]: 'Disabled',
    [PROMO_STATE.NONE]: 'Not enrolled',
};

// Background hex passed directly to design-elements' <Pill color="...">,
// matching the literal-hex convention WhitelistEditor already uses.
export const PROMO_STATE_COLORS = {
    [PROMO_STATE.ACTIVE]: '#e3f4ea',
    [PROMO_STATE.EXHAUSTED]: '#fbf1dc',
    [PROMO_STATE.EXPIRED]: '#fbf1dc',
    [PROMO_STATE.PENDING]: '#eef2f7',
    [PROMO_STATE.INACTIVE]: '#eef2f7',
    [PROMO_STATE.DISABLED]: '#eef2f7',
    [PROMO_STATE.NONE]: '#eef2f7',
};

// Base columns for the drawer's member roster table. PromoGroupDrawer
// appends an "actions" (remove) column itself, since that needs the
// onRemoveMember handler these plain data columns don't have access to.
export const memberColumns = [
    {
        title: 'Member ID',
        dataIndex: 'member_id',
        key: 'member_id',
        render: (value) => (
            <Text as="span" size="s">
                {value}
            </Text>
        ),
    },
    {
        title: 'Joined platform',
        dataIndex: 'platform_joined_at',
        key: 'platform_joined_at',
        render: (value) => (
            <Text as="span" size="s">
                {formatLongDate(value)}
            </Text>
        ),
    },
    {
        title: 'Promo starts',
        dataIndex: 'anchor_at',
        key: 'anchor_at',
        render: (value) => (
            <Text as="span" size="s">
                {formatLongDate(value)}
            </Text>
        ),
    },
    {
        title: 'Credits',
        key: 'credits',
        render: (_, row) => (
            <Text as="span" size="s">
                {row.credits_remaining != null && row.free_advance_count != null
                    ? `${row.credits_remaining} / ${row.free_advance_count}`
                    : '—'}
            </Text>
        ),
    },
    {
        title: 'Expires',
        dataIndex: 'expires_at',
        key: 'expires_at',
        render: (value) => (
            <Text as="span" size="s">
                {formatLongDate(value)}
            </Text>
        ),
    },
    {
        title: 'Status',
        dataIndex: 'state',
        key: 'state',
        render: (state) => (
            <Pill
                color={PROMO_STATE_COLORS[state] || PROMO_STATE_COLORS.pending}
                text={PROMO_STATE_LABELS[state] || 'Pending'}
                textSize="xs"
            />
        ),
    },
];

export const DEFAULT_PROMO_DRAFT = {
    label: '',
    free_advance_count: 12,
    window_value: 3,
    window_unit: 'months',
    enabled: true,
};

export const DEFAULT_PROMO_DRAWER = {
    open: false,
    mode: 'create', // 'create' | 'edit'
    promoId: null,
    draft: DEFAULT_PROMO_DRAFT,
    members: [], // staged members: [{ member_id, ...existingFieldsIfLoaded }]
    removedMemberIds: [],
    loading: false,
    saving: false,
    error: null,
};
